import React, { useRef, useState } from 'react';
import { ShareDialog } from './share-dialog';
import { shareSocialPost } from 'src/api/services/social-share-service';
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Tooltip,
  Box,
  LinearProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'src/text';

import { configurationPaperSx } from '../styles';
import { LocationSelect } from 'src/components/location-select';
import { HASHTAGS_MIN, HASHTAGS_MAX } from '../constants';
import { useAspectRatioOptions } from '../hooks/use-aspect-ratio';
import { useNumberInput } from '../hooks/use-number-input';
import { useCampaigns } from '../campaigns-context';
import type { Platform } from '../types';
import {
  MediaSelect,
  ModeSelect,
  PlatformSelect,
  ToneSelect,
} from './components';
import { useAppAuth } from 'src/context/use-app-auth';
import { uploadCampaignMedia } from 'src/api/services/media-upload';

function isErrorWithMessage(err: unknown): err is { message: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  );
}

type ConfigurationPanelProps = {
  titleOverride?: string;
  enableEmailOption?: boolean;
  defaultPlatform?: Platform;
  disableHashtags?: boolean;
  hideShareActions?: boolean;
  hideLocationSelect?: boolean;
};

export const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  titleOverride,
  enableEmailOption,
  defaultPlatform,
  disableHashtags,
  hideShareActions,
  hideLocationSelect,
}) => {
  const { campaignLabels } = useTranslation() as {
    campaignLabels?: Record<string, string>;
  };
  const {
    platform,
    setPlatform,
    mode,
    setMode,
    sourceUrl,
    setSourceUrl,
    freeText,
    setFreeText,
    hashtagsLimit,
    setHashtagsLimit,
    tone,
    setTone,
    allowEmoji,
    setAllowEmoji,
    media,
    setMedia,
    mediaUrl,
    setMediaUrl,
    approxChars,
    setApproxChars,
    loading,
    generate,
    aspectRatio,
    setAspectRatio,
    imageQuality,
    setImageQuality,
    guidelines,
    setGuidelines,
    jsonOutput,
    quota,
  } = useCampaigns();

  const ratioOptions = useAspectRatioOptions(platform);
  const onHashtagsChange = useNumberInput(setHashtagsLimit);
  const onApproxCharsChange = useNumberInput(setApproxChars);
  const { location } = useAppAuth();
  const platformInitializedRef = useRef(false);

  React.useEffect(() => {
    if (
      enableEmailOption &&
      !platformInitializedRef.current &&
      defaultPlatform
    ) {
      setPlatform(defaultPlatform);
      platformInitializedRef.current = true;
    }
  }, [defaultPlatform, enableEmailOption, setPlatform]);

  // Share dialog and state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const handleShareDialogOpen = () => setShareDialogOpen(true);
  const handleShareDialogClose = () => setShareDialogOpen(false);

  async function handleShareConfirm() {
    if (!jsonOutput || !jsonOutput.campaignId) return;
    setShareLoading(true);
    setShareError(null);
    setShareSuccess(false);

    if (jsonOutput?.post?.platform === 'whatsapp') {
      if (navigator.share) {
        try {
          await navigator.share({
            title: document.title,
            text: 'Check out this great article!',
            url: document.URL,
          });
        } catch (error) {
          console.error('Error sharing:', error);
        }
      } else {
        // Fallback for unsupported browsers
        alert('Sharing is not supported in your browser.');
      }

      setShareError('Sharing WhatsApp posts via web is not supported yet.');
      setShareLoading(false);
      return;
    }

    try {
      const res = await shareSocialPost(jsonOutput, location?.key);
      if (!res?.success) throw new Error('Failed to share post');
      setShareSuccess(true);
      setShareDialogOpen(false);
    } catch (e: unknown) {
      if (isErrorWithMessage(e)) {
        setShareError(e.message);
      } else {
        setShareError('Failed to share');
      }
    } finally {
      setShareLoading(false);
    }
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploadingMedia(true);
      const url = await uploadCampaignMedia(file, location?.key);
      setMediaUrl(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Media upload failed', e);
    } finally {
      setUploadingMedia(false);
      // Reset the input so selecting the same file again works
      event.target.value = '';
    }
  }

  return (
    <Paper elevation={1} sx={configurationPaperSx}>
      {!hideLocationSelect && (
        <Box sx={{ minWidth: 280 }}>
          <LocationSelect />
        </Box>
      )}

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5">
          {titleOverride || campaignLabels?.title || 'Social Media Post'}
        </Typography>
        <Tooltip
          title={
            campaignLabels?.info || 'Configure and generate social media posts.'
          }
          placement="right"
          arrow>
          <IconButton
            size="small"
            aria-label={campaignLabels?.aboutAria || 'About campaigns'}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Platform */}

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <PlatformSelect
          label={campaignLabels?.platform || 'Platform'}
          value={platform}
          onChange={setPlatform}
          enableEmailOption={enableEmailOption}
          disableNonEmail={enableEmailOption}
        />

        {/* Input Source Mode */}
        <ModeSelect
          label={campaignLabels?.sourceMode || 'Input Source'}
          value={mode}
          onChange={setMode}
          urlLabel={campaignLabels?.modeUrl || 'URL'}
          textLabel={campaignLabels?.modeText || 'Free Text'}
        />
      </Box>

      {/* URL or Free Text */}
      {mode === 'url' ? (
        <TextField
          label={campaignLabels?.sourceUrl || 'Source URL'}
          placeholder="https://example.com/article"
          size="small"
          fullWidth
          value={sourceUrl}
          onChange={e => setSourceUrl(e.target.value)}
        />
      ) : (
        <TextField
          label={campaignLabels?.freeText || 'Post Ideas / Draft'}
          placeholder={
            campaignLabels?.ideasPlaceholder || 'Write ideas or paste text...'
          }
          size="small"
          fullWidth
          multiline
          minRows={4}
          value={freeText}
          onChange={e => setFreeText(e.target.value)}
        />
      )}

      {/* Brand Guidelines (local, editable default) */}
      <TextField
        label={campaignLabels?.guidelines || 'Brand Guidelines'}
        placeholder={
          campaignLabels?.guidelinesPlaceholder ||
          'Provide brand tone, do/don’t, disclaimers, etc.'
        }
        size="small"
        fullWidth
        multiline
        minRows={6}
        value={guidelines}
        onChange={e => setGuidelines(e.target.value)}
      />

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        {/* Hashtags limit (guard against NaN when input is cleared) */}
        <TextField
          label={campaignLabels?.hashtagsLimit || 'Hashtags Limit'}
          type="number"
          size="small"
          fullWidth
          value={hashtagsLimit}
          onChange={onHashtagsChange}
          inputProps={{ min: HASHTAGS_MIN, max: HASHTAGS_MAX }}
          disabled={disableHashtags}
        />

        {/* Desired tone */}
        <ToneSelect
          label={campaignLabels?.desiredTone || 'Desired Tone'}
          value={tone}
          onChange={setTone}
          labels={{
            friendly: campaignLabels?.toneFriendly || 'Friendly',
            professional: campaignLabels?.toneProfessional || 'Professional',
            witty: campaignLabels?.toneWitty || 'Witty',
            formal: campaignLabels?.toneFormal || 'Formal',
          }}
        />
      </Box>

      {/* Emoji toggle */}
      <FormControlLabel
        control={
          <Switch checked={allowEmoji} onChange={(_, c) => setAllowEmoji(c)} />
        }
        label={campaignLabels?.allowEmoji || 'Allow Emoji'}
      />

      {/* Media selection */}
      <MediaSelect
        label={campaignLabels?.mediaOption || 'Media Source'}
        value={media}
        onChange={setMedia}
        labels={{
          none: campaignLabels?.mediaNone || 'None',
          ai: campaignLabels?.mediaAi || 'AI Generated',
          upload: campaignLabels?.mediaUpload || 'Upload',
          url: campaignLabels?.mediaUrl || 'From URL',
        }}
      />

      {/* AI image options: aspect ratio & resolution */}
      {media === 'ai' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <FormControl size="small" fullWidth>
            <InputLabel>Aspect Ratio</InputLabel>
            <Select
              label="Aspect Ratio"
              value={aspectRatio}
              onChange={e =>
                setAspectRatio(
                  e.target.value as '1:1' | '3:4' | '4:3' | '9:16' | '16:9',
                )
              }>
              {ratioOptions.map(opt => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Resolution</InputLabel>
            <Select
              label="Resolution"
              value={imageQuality}
              onChange={e =>
                setImageQuality(e.target.value as 'standard' | 'high')
              }>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {media === 'url' && (
        <TextField
          label={campaignLabels?.imageUrl || 'Image / Video URL'}
          placeholder="https://example.com/image-or-video"
          size="small"
          fullWidth
          value={mediaUrl}
          onChange={e => setMediaUrl(e.target.value)}
        />
      )}
      {media === 'upload' && (
        <Button component="label" variant="outlined" size="small">
          {uploadingMedia
            ? campaignLabels?.uploadingButton || 'Uploading…'
            : campaignLabels?.uploadButton || 'Upload File'}
          <input
            hidden
            accept="image/*,video/*"
            type="file"
            onChange={handleFileChange}
          />
        </Button>
      )}

      {/* Approximate character count (guard against NaN) */}
      <TextField
        label={campaignLabels?.approxChars || 'Approximate Character Count'}
        type="number"
        size="small"
        fullWidth
        value={approxChars}
        onChange={onApproxCharsChange}
      />

      {/* Quota indicator when using AI images */}
      {media === 'ai' && (
        <Typography
          variant="body2"
          color={
            quota && quota.remaining !== 'unlimited' && quota.remaining <= 0
              ? 'error'
              : 'text.secondary'
          }>
          {quota?.loading
            ? 'Checking image quota…'
            : `Images remaining this month: ${quota?.remaining ?? '—'}/${
                quota?.limit ?? 30
              }`}
        </Typography>
      )}

      <Divider sx={{ my: 1 }} />

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          disableElevation
          onClick={generate}
          disabled={
            !location?.key ||
            loading ||
            uploadingMedia ||
            (media === 'ai' &&
              !!quota &&
              quota.remaining !== 'unlimited' &&
              quota.remaining <= 0)
          }>
          {loading
            ? campaignLabels?.generating || 'Generating…'
            : campaignLabels?.generatePost || 'Generate Post'}
        </Button>

        {!hideShareActions && (
          <>
            <Tooltip
              title={campaignLabels?.shareComingSoon || 'Share your post'}>
              <Button
                variant="outlined"
                disabled={!jsonOutput || shareLoading}
                onClick={handleShareDialogOpen}>
                {shareLoading
                  ? campaignLabels?.sharing || 'Sharing…'
                  : campaignLabels?.share || 'Share'}
              </Button>
            </Tooltip>
            <ShareDialog
              open={shareDialogOpen}
              loading={shareLoading}
              onClose={handleShareDialogClose}
              onConfirm={handleShareConfirm}
              labels={{
                title: campaignLabels?.shareDialogTitle || 'Share Post',
                content:
                  campaignLabels?.shareDialogContent ||
                  'Are you sure you want to share this post?',
                confirm: campaignLabels?.shareDialogConfirm || 'Yes, Share',
                cancel: campaignLabels?.shareDialogCancel || 'Cancel',
                loading: campaignLabels?.sharing || 'Sharing…',
              }}
            />
          </>
        )}
      </Stack>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress aria-label="Generating post in progress" />
        </Box>
      )}

      {!hideShareActions && (
        <>
          {shareError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {shareError}
            </Typography>
          )}
          {shareSuccess && (
            <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
              {campaignLabels?.shareSuccess || 'Post shared successfully!'}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};
